/**
 * Friends, presence and invites (design/v2.md §2.5).
 *
 * Everything here arrives over `SocialRoom` as addressed messages, never as
 * shared state, so the store is the client's private copy of what the server
 * chose to tell *this* account. Presence in particular is a map keyed by friend
 * id and nothing else — the server never sends a room id with it, so there is
 * nowhere here for one to be kept.
 *
 * The list and the presence map are deliberately separate. `social.hello` brings
 * both at once, but afterwards presence changes ten times for every list change,
 * and merging them would rewrite every row of the panel each time somebody's dot
 * turned green.
 */

import type { StateCreator } from 'zustand';

import type { Friend, FriendRequest, PresenceState, RoomInvite } from '@ordago/shared';

import type { Slices, SocialSlice } from './types';

/** Invites are short-lived; more than a handful on screen is a bug elsewhere. */
const MAX_INVITES = 5;

export const createSocialSlice: StateCreator<Slices, [], [], SocialSlice> = (set, get) => ({
  social: {
    connection: 'idle',
    friends: [],
    incoming: [],
    outgoing: [],
    presence: {},
    invites: [],
    panelOpen: false,

    setConnection: (connection) => {
      set((s) => ({ social: { ...s.social, connection } }));
    },

    /** `social.hello`, and every REST refresh of the list. */
    setFriends: (friends) => {
      set((s) => ({
        social: {
          ...s.social,
          friends,
          // Seeded from the list the server just sent, so a friend who was
          // already playing when we signed in has the right dot immediately
          // rather than only after their next transition.
          presence: Object.fromEntries(friends.map((f) => [f.userId, f.presence])),
        },
      }));
    },

    setRequests: (incoming, outgoing) => {
      set((s) => ({ social: { ...s.social, incoming, outgoing } }));
    },

    setPresence: (userId, state) => {
      set((s) => ({
        social: { ...s.social, presence: { ...s.social.presence, [userId]: state } },
      }));
    },

    addIncoming: (request) => {
      set((s) => ({
        social: {
          ...s.social,
          incoming: s.social.incoming.some((r) => r.id === request.id)
            ? s.social.incoming
            : [request, ...s.social.incoming],
        },
      }));
    },

    /**
     * A newly accepted friend. Also drops them from both request lists, because
     * the request that produced them no longer exists on the server.
     */
    addFriend: (friend) => {
      set((s) => ({
        social: {
          ...s.social,
          friends: s.social.friends.some((f) => f.userId === friend.userId)
            ? s.social.friends
            : [...s.social.friends, friend].sort((a, b) => a.username.localeCompare(b.username)),
          presence: { ...s.social.presence, [friend.userId]: friend.presence },
          incoming: s.social.incoming.filter((r) => r.userId !== friend.userId),
          outgoing: s.social.outgoing.filter((r) => r.userId !== friend.userId),
        },
      }));
    },

    /**
     * One message covers unfriend, decline and block: the server deliberately
     * says only "this relationship is over", never which of the three it was,
     * because "you were blocked" is information a blocked person can act on.
     */
    removeFriend: (userId) => {
      set((s) => {
        const presence = { ...s.social.presence };
        delete presence[userId];
        return {
          social: {
            ...s.social,
            friends: s.social.friends.filter((f) => f.userId !== userId),
            incoming: s.social.incoming.filter((r) => r.userId !== userId),
            outgoing: s.social.outgoing.filter((r) => r.userId !== userId),
            presence,
          },
        };
      });
    },

    pushInvite: (invite) => {
      set((s) => {
        const kept = s.social.invites.filter((i) => i.roomId !== invite.roomId);
        const next = [...kept, invite];
        return {
          social: {
            ...s.social,
            invites: next.length > MAX_INVITES ? next.slice(next.length - MAX_INVITES) : next,
          },
        };
      });
    },

    dismissInvite: (inviteId) => {
      set((s) => ({
        social: { ...s.social, invites: s.social.invites.filter((i) => i.inviteId !== inviteId) },
      }));
    },

    setPanelOpen: (panelOpen) => {
      set((s) => ({ social: { ...s.social, panelOpen } }));
    },

    presenceOf: (userId) => get().social.presence[userId] ?? 'offline',

    reset: () => {
      set((s) => ({
        social: {
          ...s.social,
          connection: 'idle',
          friends: [],
          incoming: [],
          outgoing: [],
          presence: {},
          invites: [],
          panelOpen: false,
        },
      }));
    },
  },
});

/** Friends who can be invited right now, in the order the panel shows them. */
export function invitableFriends(
  friends: Friend[],
  presence: Record<string, PresenceState>,
): Friend[] {
  // `playing` is excluded on purpose: they are already at a table, and the
  // server would refuse the invite with `INVITE_TARGET_BUSY`. Offering a button
  // that is known to fail is worse than not offering one.
  return friends.filter((f) => {
    const state = presence[f.userId] ?? 'offline';
    return state === 'online' || state === 'lobby';
  });
}

/** Live invites only; the server's expiry is authoritative, this just hides. */
export function liveInvites(invites: RoomInvite[], now: number): RoomInvite[] {
  return invites.filter((invite) => invite.expiresAt > now);
}

/** The navbar badge: requests waiting on an answer from me. */
export function pendingBadgeCount(incoming: FriendRequest[], invites: RoomInvite[]): number {
  return incoming.length + invites.length;
}
